import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App.tsx'
import { ErrorBoundary } from '@components/ErrorBoundary.tsx'
import { AuthProvider } from '@auth/AuthContext.tsx'
import { ModeProvider } from '@mode/ModeContext.tsx'
import { Router } from '@router'
import { readSnapshot } from '@auth/snapshot.ts'
import { hydrateCache } from '@cache/resources.ts'
import { useHideSplash } from '@hooks/useHideSplash.ts'
import { fitToKeyboard } from '@utils/keyboard.ts'
import './styles/globals.css'

// iOS Safari ignores user-scalable=no in the viewport meta, so pinch zoom is
// stopped at its WebKit-only gesture event instead.
document.addEventListener('gesturestart', (e) => e.preventDefault())

fitToKeyboard()

const el = document.getElementById('root')
if (!el) throw new Error('#root not found')

// Outside the ErrorBoundary, so a crash still hides the native splash and
// shows the fallback.
const Root = () => {
	useHideSplash()
	return (
		<ErrorBoundary>
			<Router>
				<AuthProvider>
					<ModeProvider>
						<App />
					</ModeProvider>
				</AuthProvider>
			</Router>
		</ErrorBoundary>
	)
}

const render = () => {
	createRoot(el).render(
		<StrictMode>
			<Root />
		</StrictMode>,
	)
}

// Fill the cache before the first paint, so everything downstream can treat it
// as synchronously present. A few hundred KB of JSON against the network round
// trip the app waits on today — and it's bounded by a timeout, so a browser
// that can't open IndexedDB just starts cold.
void hydrateCache(readSnapshot()?.user.id ?? null)
	.catch(() => undefined)
	.then(render)
